import { useEffect, useRef, useState } from 'react';
import { useGlobal } from '../../context/GlobalContext';
import { getStrapiMedia, getStrapiMediaAttributes, submitCotizacion } from '../../lib/strapi';
import styles from './Cotizador.module.css';

const COUNTRY_CODES = [
  { code: '+52', flag: '🇲🇽' },
  { code: '+1', flag: '🇺🇸' },
  { code: '+57', flag: '🇨🇴' },
  { code: '+34', flag: '🇪🇸' },
  { code: '+54', flag: '🇦🇷' },
  { code: '+56', flag: '🇨🇱' },
  { code: '+55', flag: '🇧🇷' },
  { code: '+51', flag: '🇵🇪' },
];

const DEFAULT_FORM_FIELDS = [
  { label: 'Nombre completo', name: 'fullName', type: 'text', required: true },
  { label: 'E-mail', name: 'email', type: 'email', required: true, halfWidth: true },
  { label: 'Teléfono', name: 'phone', type: 'phone', required: true, halfWidth: true },
  { label: 'Nombre del vendedor', name: 'vendedor', type: 'text', required: false },
];

const FINAL_STEP_LABEL = 'Enviar cotización';
const DEFAULT_HERO_SUBTITLE = 'Está a unos pasos de su departamento ideal.';
const SCHEME_BATCH_SIZE = 3;
const DEFAULT_STEP_COPY = {
  step1: 'Seleccione alguna de nuestras opciones de pago que mejor se adapte a su presupuesto y haga click en calcular para continuar:',
  step2: 'Aquí se presenta el resultado de su cotización:',
  step3: 'Proporcione sus datos de contacto para poder enviar a su correo la cotización realizada:',
  step4: 'Su cotización ha sido enviada a la dirección de correo proporcionada. No dude en contactarnos por cualquier duda o si desea más información.',
};
const FEATURE_PRIORITY = [
  { patterns: ['precio'], fallbackLabel: 'Precio' },
  { patterns: ['total m2', 'm2 totales', 'm² totales'], fallbackLabel: 'm² totales' },
  { patterns: ['depto m2', 'm2 interiores', 'interior', 'interiores'], fallbackLabel: 'm² interiores' },
  { patterns: ['terraza'], fallbackLabel: 'm² terraza' },
  { patterns: ['bodega'], fallbackLabel: 'Bodega' },
  { patterns: ['tipo'], fallbackLabel: 'Tipo' },
  { patterns: ['baño', 'bano'], fallbackLabel: 'Baños' },
  { patterns: ['torre'], fallbackLabel: 'Torre' },
  { patterns: ['piso'], fallbackLabel: 'Piso' },
];

const normalizeText = (value = '') => String(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

const toNumber = (value, fallback = 0) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  const raw = String(value ?? '').replace(/\s+/g, '').replace(/[^\d,.-]/g, '');
  if (!raw) return fallback;

  let normalized = raw;
  const hasComma = normalized.includes(',');
  const hasDot = normalized.includes('.');

  if (hasComma && hasDot) {
    normalized = normalized.replace(/,/g, '');
  } else if (hasComma) {
    normalized = normalized.replace(/,/g, '.');
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));

const formatCurrency = (value) => new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(Number.isFinite(value) ? value : 0);

const formatPercent = (value) => `${Number(value || 0).toFixed(1).replace(/\.0$/, '')}%`;

const getMediaUrl = (media) => {
  const url = media?.url || media?.data?.url || media?.data?.attributes?.url || null;
  return url ? getStrapiMedia(url) : null;
};

const getFeatureEntry = (item, patterns = []) => {
  const normalizedPatterns = patterns.map(normalizeText);

  return (item?.features || []).find((feature) => {
    const attribute = normalizeText(feature?.atributo);
    return normalizedPatterns.some((pattern) => attribute.includes(pattern));
  }) || null;
};

const getFeatureValue = (item, patterns = []) => getFeatureEntry(item, patterns)?.valor || '';

const buildSummaryFeatures = (item) => {
  const usedKeys = new Set();
  const summary = [];

  FEATURE_PRIORITY.forEach(({ patterns, fallbackLabel }) => {
    const match = getFeatureEntry(item, patterns);
    if (!match) return;

    const key = normalizeText(match.atributo);
    if (usedKeys.has(key)) return;

    usedKeys.add(key);
    summary.push({
      label: match.atributo || fallbackLabel,
      value: match.valor || '—',
    });
  });

  if (summary.length >= 6) return summary.slice(0, 6);

  (item?.features || []).forEach((feature) => {
    if (summary.length >= 6) return;
    const key = normalizeText(feature?.atributo);
    if (!key || usedKeys.has(key)) return;

    usedKeys.add(key);
    summary.push({
      label: feature?.atributo || 'Dato',
      value: feature?.valor || '—',
    });
  });

  return summary;
};

const groupFields = (fields) => {
  const groups = [];
  let index = 0;

  while (index < fields.length) {
    if (fields[index]?.halfWidth && fields[index + 1]?.halfWidth) {
      groups.push({ type: 'row', fields: [fields[index], fields[index + 1]] });
      index += 2;
    } else {
      groups.push({ type: 'single', field: fields[index] });
      index += 1;
    }
  }

  return groups;
};

const buildInitialFormValues = (fields, itemTitle) => fields.reduce((accumulator, field) => {
  if (!field?.name) return accumulator;

  accumulator[field.name] = field.type === 'items' ? itemTitle || '' : '';
  return accumulator;
}, {});

const hasRequiredFieldValue = (field, values) => {
  if (!field?.required) return true;

  const key = typeof field?.name === 'string' ? field.name.trim() : '';
  if (!key) return true;

  const value = values[key];

  if (Array.isArray(value)) {
    return value.some((entry) => String(entry ?? '').trim());
  }

  return String(value ?? '').trim().length > 0;
};

const getFirstMissingRequiredField = (fields, values) => (
  (fields || []).find((field) => !hasRequiredFieldValue(field, values)) || null
);

const getFieldValidationMessage = (field, values) => {
  const key = typeof field?.name === 'string' ? field.name.trim() : '';
  if (!key) return '';

  const rawValue = values[key];
  const value = Array.isArray(rawValue) ? rawValue.join(' ').trim() : String(rawValue ?? '').trim();

  if (field?.required && !value) {
    return 'Completa este campo.';
  }

  if (field?.type === 'email' && value) {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(value)) {
      return 'Ingresa un correo valido.';
    }
  }

  return '';
};

const validateFields = (fields, values) => (fields || []).reduce((accumulator, field) => {
  const message = getFieldValidationMessage(field, values);
  if (message && field?.name) {
    accumulator[field.name] = message;
  }
  return accumulator;
}, {});

const extractContactData = (fields, values, phoneCountry) => {
  const entries = fields
    .map((field) => {
      const key = typeof field?.name === 'string' ? field.name.trim() : '';

      return {
        field,
        key,
        value: key ? values[key] ?? '' : '',
        meta: normalizeText(`${field?.label || ''} ${field?.placeholder || ''} ${field?.name || ''}`),
      };
    })
    .filter(({ key }) => Boolean(key));

  const findEntry = (predicate) => entries.find((entry) => entry.value && predicate(entry));
  const textEntries = entries.filter(({ field, value }) => field?.type === 'text' && value);

  const nombre = findEntry(({ field, meta }) => field?.type === 'text' && /(nombre completo|full name|nombre|name|cliente)/.test(meta))?.value
    || textEntries[0]?.value
    || '';
  const correo = findEntry(({ field }) => field?.type === 'email')?.value || '';
  const rawPhone = findEntry(({ field }) => field?.type === 'phone')?.value || '';
  const telefono = rawPhone ? `${phoneCountry.code} ${rawPhone}` : '';
  const vendedor = findEntry(({ meta }) => /(vendedor|asesor|broker|advisor|agente|seller)/.test(meta))?.value || '';

  const formPayload = entries.reduce((accumulator, entry) => {
    accumulator[entry.key] = entry.value;
    return accumulator;
  }, {});

  return {
    nombre,
    correo,
    telefono,
    vendedor,
    formPayload,
  };
};

const getExtraKey = (extra, index) => `${normalizeText(extra?.nombre || 'extra')}-${index}`;

const normalizeProjectId = (value = '') => {
  const normalized = String(value || '').trim().replace(/\s+/g, '_');
  return normalized || undefined;
};

const Cotizador = ({ data, item, isOpen = false, onClose, page = null, source = '' }) => {
  const { companyInfo } = useGlobal();
  const rawFields = Array.isArray(data?.campos)
    ? data.campos
    : (Array.isArray(data?.fields) ? data.fields : []);
  const fields = rawFields.length > 0 ? rawFields : DEFAULT_FORM_FIELDS;
  const schemes = Array.isArray(data?.esquemasDePago) ? data.esquemasDePago : [];
  const extras = (Array.isArray(data?.extras) ? data.extras : []).map((extra, index) => ({
    ...extra,
    key: getExtraKey(extra, index),
    precio: toNumber(extra?.precio, 0),
  }));
  const groupedFields = groupFields(fields);
  const backgroundImage = getMediaUrl(data?.imagen);
  const summaryFeatures = buildSummaryFeatures(item);
  const priceValue = toNumber(getFeatureValue(item, ['precio']), 0);
  const itemType = getFeatureValue(item, ['tipo']) || page?.titulo || '';
  const stepLabels = [
    data?.paso1Label || 'Opciones de pago',
    data?.paso2Label || 'Calcular',
    data?.paso3Label || 'Preparar cotización',
    data?.paso4Label || FINAL_STEP_LABEL,
  ];
  const stepCopy = {
    step1: data?.paso1Texto || DEFAULT_STEP_COPY.step1,
    step2: data?.paso2Texto || DEFAULT_STEP_COPY.step2,
    step3: data?.paso3Texto || DEFAULT_STEP_COPY.step3,
    step4: data?.paso4Texto || data?.mensajeExito || DEFAULT_STEP_COPY.step4,
  };
  const minDeferredMonths = Math.max(1, Math.round(toNumber(data?.mesesDiferidoMin, 1)));
  const maxDeferredMonths = Math.max(minDeferredMonths, Math.round(toNumber(data?.mesesDiferidoMax, 36)));
  const maxCustomDiscount = clampNumber(toNumber(data?.maxDescuento, 0), 0, 100);
  const initialDeferredMonths = clampNumber(
    Math.round(toNumber(schemes[0]?.mesesDiferido, toNumber(data?.mesesDiferidoDefault, 12))),
    minDeferredMonths,
    maxDeferredMonths
  );
  const heroSubtitle = data?.subtitulo || DEFAULT_HERO_SUBTITLE;
  const derivedProjectId = normalizeProjectId(
    data?.projectId || page?.projectId || companyInfo?.companyName
  );
  const modalTitle = data?.titulo || 'Cotizador de pagos';

  const [screen, setScreen] = useState(0);
  const [selectedSchemeIndex, setSelectedSchemeIndex] = useState(-1);
  const [isSchemeMenuOpen, setIsSchemeMenuOpen] = useState(false);
  const [visibleSchemeCount, setVisibleSchemeCount] = useState(SCHEME_BATCH_SIZE);
  const [selectedExtraKeys, setSelectedExtraKeys] = useState([]);
  const [customDiscount, setCustomDiscount] = useState(0);
  const [customDeferredMonths, setCustomDeferredMonths] = useState(null);
  const [phoneCountry, setPhoneCountry] = useState(COUNTRY_CODES[0]);
  const [formValues, setFormValues] = useState(() => buildInitialFormValues(fields, item?.titulo));
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const formRef = useRef(null);

  const selectedScheme = selectedSchemeIndex >= 0 ? (schemes[selectedSchemeIndex] || null) : null;
  const selectedExtras = extras.filter((extra) => selectedExtraKeys.includes(extra.key));
  const contactData = extractContactData(fields, formValues, phoneCountry);
  const defaultDeferredMonths = clampNumber(
    Math.round(toNumber(selectedScheme?.mesesDiferido, initialDeferredMonths)),
    minDeferredMonths,
    maxDeferredMonths
  );
  const mesesDiferido = customDeferredMonths ?? defaultDeferredMonths;
  const extrasTotal = selectedExtras.reduce((sum, extra) => sum + extra.precio, 0);
  const grossTotal = priceValue + extrasTotal;
  const schemeDiscountPct = clampNumber(toNumber(selectedScheme?.descuento, 0), 0, 100);
  const customDiscountPct = data?.permitirDescuentoCustom
    ? clampNumber(toNumber(customDiscount, 0), 0, maxCustomDiscount)
    : 0;
  const totalDiscountPct = clampNumber(schemeDiscountPct + customDiscountPct, 0, 100);
  const discountAmount = grossTotal * (totalDiscountPct / 100);
  const totalToPay = Math.max(0, grossTotal - discountAmount);
  const upfrontPct = clampNumber(toNumber(selectedScheme?.anticipo, 0), 0, 100);
  const finalPct = clampNumber(toNumber(selectedScheme?.finiquito, 0), 0, 100);
  const deferredPct = Math.max(0, 100 - upfrontPct - finalPct);
  const upfrontAmount = totalToPay * (upfrontPct / 100);
  const deferredAmount = totalToPay * (deferredPct / 100);
  const monthlyCount = Math.max(0, Math.round(toNumber(selectedScheme?.mensualidades, 0)));
  const monthlyAmount = monthlyCount > 0 ? deferredAmount / monthlyCount : 0;
  const finalPayment = totalToPay * (finalPct / 100);
  const calculation = {
    grossTotal,
    extrasTotal,
    schemeDiscountPct,
    customDiscountPct,
    totalDiscountPct,
    discountAmount,
    totalToPay,
    upfrontPct,
    deferredPct,
    finalPct,
    upfrontAmount,
    deferredAmount,
    monthlyCount,
    monthlyAmount,
    finalPayment,
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (isSchemeMenuOpen) {
          setIsSchemeMenuOpen(false);
          return;
        }

        onClose?.();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isSchemeMenuOpen, onClose]);

  if (!data || !isOpen || !item) {
    return null;
  }

  const handleFieldChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;

      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const toggleExtra = (extraKey) => {
    setSelectedExtraKeys((prev) => (
      prev.includes(extraKey)
        ? prev.filter((key) => key !== extraKey)
        : [...prev, extraKey]
    ));
  };

  const renderField = (field) => {
    const value = formValues[field.name] ?? '';
    const label = field.label || field.name;
    const hasError = Boolean(fieldErrors[field.name]);
    const errorId = hasError ? `cotizador-error-${field.name}` : undefined;

    switch (field.type) {
      case 'text':
      case 'number':
        return (
          <input
            className={`${styles.cotizador__input} ${hasError ? styles['cotizador__input--error'] : ''}`}
            type={field.type === 'number' ? 'number' : 'text'}
            name={field.name}
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(event) => handleFieldChange(field.name, event.target.value)}
            required={field.required}
            aria-invalid={hasError}
            aria-describedby={errorId}
          />
        );

      case 'email':
        return (
          <input
            className={`${styles.cotizador__input} ${hasError ? styles['cotizador__input--error'] : ''}`}
            type="email"
            name={field.name}
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(event) => handleFieldChange(field.name, event.target.value)}
            required={field.required}
            aria-invalid={hasError}
            aria-describedby={errorId}
          />
        );

      case 'phone':
        return (
          <div className={`${styles.cotizador__phoneWrapper} ${hasError ? styles['cotizador__phoneWrapper--error'] : ''}`}>
            <div className={styles.cotizador__phoneCode}>
              <select
                className={styles.cotizador__phoneSelect}
                value={phoneCountry.code}
                onChange={(event) => {
                  const nextCountry = COUNTRY_CODES.find((entry) => entry.code === event.target.value);
                  if (nextCountry) setPhoneCountry(nextCountry);
                }}
                aria-label="Código de país"
              >
                {COUNTRY_CODES.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.code}
                  </option>
                ))}
              </select>
            </div>
            <input
              className={styles.cotizador__phoneInput}
              type="tel"
              name={field.name}
              placeholder={field.placeholder || ''}
              value={value}
              onChange={(event) => handleFieldChange(field.name, event.target.value)}
              required={field.required}
              aria-invalid={hasError}
              aria-describedby={errorId}
            />
          </div>
        );

      case 'select':
        return (
          <select
            className={`${styles.cotizador__select} ${hasError ? styles['cotizador__select--error'] : ''}`}
            name={field.name}
            value={value}
            onChange={(event) => handleFieldChange(field.name, event.target.value)}
            required={field.required}
            aria-invalid={hasError}
            aria-describedby={errorId}
          >
            <option value="">{field.placeholder || label}</option>
            {(field.options || []).map((option, index) => (
              <option key={`${field.name}-${index}`} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'chips':
        return (
          <div className={`${styles.cotizador__chips} ${hasError ? styles['cotizador__chips--error'] : ''}`}>
            {(field.options || []).map((option, index) => {
              const isSelected = value === option.value;
              return (
                <button
                  key={`${field.name}-${index}`}
                  type="button"
                  className={`${styles.cotizador__chip} ${isSelected ? styles['cotizador__chip--active'] : ''}`}
                  onClick={() => handleFieldChange(field.name, isSelected ? '' : option.value)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        );

      case 'textarea':
        return (
          <textarea
            className={`${styles.cotizador__textarea} ${hasError ? styles['cotizador__textarea--error'] : ''}`}
            name={field.name}
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(event) => handleFieldChange(field.name, event.target.value)}
            required={field.required}
            rows={4}
            aria-invalid={hasError}
            aria-describedby={errorId}
          />
        );

      case 'items':
        return (
          <input
            className={`${styles.cotizador__input} ${hasError ? styles['cotizador__input--error'] : ''}`}
            type="text"
            name={field.name}
            value={item?.titulo || value}
            readOnly
            disabled
            aria-invalid={hasError}
            aria-describedby={errorId}
          />
        );

      default:
        return null;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setToast(null);

    if (!selectedScheme) {
      setToast({ type: 'error', message: 'Selecciona un esquema de pago para continuar.' });
      return;
    }

    const formElement = formRef.current || event.currentTarget;

    const validationErrors = validateFields(fields, formValues);
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setToast({
        type: 'error',
        message: 'Completa los campos marcados antes de enviar.',
      });

      const firstInvalidField = fields.find((field) => validationErrors[field?.name]);
      if (firstInvalidField?.name && formElement) {
        const invalidControl = formElement.querySelector(`[name="${firstInvalidField.name}"]`)
          || formElement.querySelector(`[data-field-name="${firstInvalidField.name}"] button`)
          || formElement.querySelector(`[data-field-name="${firstInvalidField.name}"]`);
        invalidControl?.focus();
      }

      return;
    }

    setFieldErrors({});

    const missingRequiredField = getFirstMissingRequiredField(fields, formValues);
    if (missingRequiredField) {
      setToast({
        type: 'error',
        message: `Completa el campo ${missingRequiredField.label || 'requerido'} antes de enviar.`,
      });
      return;
    }

    setSubmitting(true);

    try {
      await submitCotizacion({
        nombre: contactData.nombre,
        correo: contactData.correo,
        telefono: contactData.telefono,
        vendedor: contactData.vendedor,
        url: typeof window !== 'undefined' ? window.location.href : '',
        estado: 'enviada',
        descuentoGlobal: calculation.totalDiscountPct,
        totalFinal: calculation.totalToPay,
        projectId: derivedProjectId,
        info: {
          pagina: page?.titulo || '',
          slug: page?.slug || source || '',
          unidad: item?.titulo || '',
          propiedad: page?.titulo || item?.titulo || '',
          monto: priceValue,
          extras_total: calculation.extrasTotal,
          opcion_pago: selectedScheme?.nombre || '',
          descuento: calculation.discountAmount,
          descuento_porcentaje: calculation.totalDiscountPct,
          descuento_escenario: calculation.schemeDiscountPct,
          descuento_custom: calculation.customDiscountPct,
          total_pagar: calculation.totalToPay,
          enganche: calculation.upfrontAmount,
          diferido: calculation.deferredAmount,
          mensualidades: calculation.monthlyCount,
          mensualidad: calculation.monthlyAmount,
          contra_escritura: calculation.finalPayment,
          meses_diferido: mesesDiferido,
          adicionales: selectedExtras.map((extra) => ({ nombre: extra.nombre, precio: extra.precio })),
          formulario: contactData.formPayload,
          textoLegal: data?.textoLegal || undefined,
        },
        unidades: [
          {
            itemId: item?.id || undefined,
            nombre: item?.titulo || '',
            tipologia: itemType,
            precioOriginal: priceValue,
            escenarioId: selectedScheme?.id || undefined,
            descuentoEscenario: calculation.schemeDiscountPct,
            descuentoCustom: calculation.customDiscountPct,
            precioFinal: calculation.totalToPay,
            enganche: calculation.upfrontAmount,
            mensualidad: calculation.monthlyAmount,
            pagoFinal: calculation.finalPayment,
            mesesDiferido: mesesDiferido,
            adicionales: selectedExtras.map((extra) => ({ nombre: extra.nombre, precio: extra.precio })),
          },
        ],
      });

      setScreen(4);
    } catch {
      setToast({ type: 'error', message: 'No se pudo enviar la cotización. Intenta nuevamente.' });
    } finally {
      setSubmitting(false);
    }
  };

  const activeStep = screen === 0 ? 0 : screen;
  const summaryButtonLabel = stepLabels[0];
  const selectedSchemeLabel = selectedScheme?.nombre || 'Seleccione una opción';
  const visibleSchemes = schemes.slice(0, visibleSchemeCount);
  const hasMoreSchemes = visibleSchemeCount < schemes.length;
  const canCollapseSchemes = visibleSchemeCount > Math.min(SCHEME_BATCH_SIZE, schemes.length);

  return (
    <div className={styles.cotizador} data-cotizador-shell="true">
      <div className={styles.cotizador__overlay} onClick={() => onClose?.()}>
        <div className={styles.cotizador__modal} onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label={modalTitle}>
          <div
            className={styles.cotizador__heroPanel}
            style={backgroundImage ? { backgroundImage: `linear-gradient(rgba(15, 18, 24, 0.28), rgba(15, 18, 24, 0.72)), url(${backgroundImage})` } : undefined}
          >
            <div className={styles.cotizador__heroContent}>
              <div>
                <h2 className={styles.cotizador__titulo}>{modalTitle}</h2>
                <p className={styles.cotizador__subtitulo}>{heroSubtitle}</p>
              </div>

              {screen > 0 && (
                <div className={styles.cotizador__stepList}>
                  {stepLabels.map((label, index) => {
                    const stepNumber = index + 1;
                    const isDone = stepNumber < activeStep;
                    const isActive = stepNumber === activeStep;
                    const isUpcoming = !isDone && !isActive;

                    return (
                      <div
                        key={label}
                        className={`${styles.cotizador__stepItem} ${isDone ? styles['cotizador__stepItem--done'] : ''} ${isActive ? styles['cotizador__stepItem--active'] : ''} ${isUpcoming ? styles['cotizador__stepItem--upcoming'] : ''}`}
                      >
                        <span className={styles.cotizador__stepDot}>{isDone ? '✓' : String(stepNumber).padStart(2, '0')}</span>
                        <span className={styles.cotizador__stepLabel}>{label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className={styles.cotizador__panel}>
            <button type="button" className={styles.cotizador__close} onClick={() => onClose?.()} aria-label="Cerrar cotizador">
              ×
            </button>

            {screen > 0 && (
              <div className={styles.cotizador__mobileProgress} aria-label="Progreso del cotizador">
                <div className={styles.cotizador__mobileProgressMeta} aria-live="polite">
                  <span className={styles.cotizador__mobileProgressTag}>Paso {activeStep} de {stepLabels.length}</span>
                  <span className={styles.cotizador__mobileProgressLabel}>{stepLabels[activeStep - 1]}</span>
                </div>
              </div>
            )}

            {screen === 0 && (
              <div className={styles.cotizador__screen}>
                <div className={styles.cotizador__intro}>
                  <h3 className={styles.cotizador__introTitle}>{modalTitle}</h3>
                  <p className={styles.cotizador__introText}>{heroSubtitle}</p>
                </div>
                <div className={styles.cotizador__introDivider} aria-hidden="true" />

                <h3 className={styles.cotizador__summaryTitle}>{item?.titulo || 'Unidad seleccionada'}</h3>
                <div className={styles.cotizador__summaryGrid}>
                  {summaryFeatures.map((feature) => (
                    <article key={`${feature.label}-${feature.value}`} className={styles.cotizador__summaryCard}>
                      <span className={styles.cotizador__summaryLabel}>{feature.label}</span>
                      <strong className={styles.cotizador__summaryValue}>{feature.value}</strong>
                    </article>
                  ))}
                </div>

                {priceValue <= 0 && (
                  <p className={styles.cotizador__empty}>Esta unidad no tiene precio disponible para cotizar todavía.</p>
                )}

                <div className={`${styles.cotizador__footer} ${styles['cotizador__footer--end']}`}>
                  <button
                    type="button"
                    className={styles.cotizador__btnPrimary}
                    onClick={() => setScreen(1)}
                    disabled={schemes.length === 0}
                  >
                    <span className={styles.cotizador__btnText}>{summaryButtonLabel}</span>
                    <span className={styles.cotizador__btnArrow}>→</span>
                  </button>
                </div>
              </div>
            )}

            {screen === 1 && (
              <div className={styles.cotizador__screen}>
                <div className={styles.cotizador__heading}>
                  <span className={styles.cotizador__headingBadge}>01</span>
                  <div className={styles.cotizador__headingContent}>
                    <h3 className={styles.cotizador__headingTitle}>{stepLabels[0]}</h3>
                    <p className={styles.cotizador__headingText}>{stepCopy.step1}</p>
                  </div>
                </div>

                {schemes.length > 0 ? (
                  <>
                    <label className={styles.cotizador__inputLabel} htmlFor="cotizador-scheme">
                      Esquema de pago
                    </label>
                    <div
                      className={styles.cotizador__schemePicker}
                      onBlur={(event) => {
                        if (!event.currentTarget.contains(event.relatedTarget)) {
                          setIsSchemeMenuOpen(false);
                        }
                      }}
                    >
                      <button
                        id="cotizador-scheme"
                        type="button"
                        className={`${styles.cotizador__schemeTrigger} ${isSchemeMenuOpen ? styles['cotizador__schemeTrigger--open'] : ''}`}
                        onClick={() => {
                          setIsSchemeMenuOpen((prev) => {
                            const nextOpen = !prev;

                            if (nextOpen) {
                              setVisibleSchemeCount(Math.min(
                                schemes.length,
                                Math.max(SCHEME_BATCH_SIZE, selectedSchemeIndex + 1)
                              ));
                            }

                            return nextOpen;
                          });
                        }}
                        aria-haspopup="listbox"
                        aria-expanded={isSchemeMenuOpen}
                      >
                        <span className={styles.cotizador__schemeTriggerText}>{selectedSchemeLabel}</span>
                        <span
                          className={`${styles.cotizador__schemeTriggerArrow} ${isSchemeMenuOpen ? styles['cotizador__schemeTriggerArrow--open'] : ''}`}
                          aria-hidden="true"
                        >
                          ▾
                        </span>
                      </button>

                      {isSchemeMenuOpen && (
                        <div className={styles.cotizador__schemeDropdown}>
                          <div className={styles.cotizador__schemeList} role="listbox" aria-labelledby="cotizador-scheme">
                            {visibleSchemes.map((scheme, index) => {
                              const schemeIcon = getStrapiMediaAttributes(scheme?.icon, {
                                altFallback: scheme?.nombre || `Esquema ${index + 1}`,
                                titleFallback: scheme?.nombre || `Esquema ${index + 1}`,
                              });
                              const isSelected = index === selectedSchemeIndex;

                              return (
                                <button
                                  key={scheme?.id || scheme?.documentId || index}
                                  type="button"
                                  role="option"
                                  aria-selected={isSelected}
                                  className={`${styles.cotizador__schemeOption} ${isSelected ? styles['cotizador__schemeOption--selected'] : ''}`}
                                  onClick={() => {
                                    setSelectedSchemeIndex(index);
                                    setCustomDeferredMonths(null);
                                    setIsSchemeMenuOpen(false);
                                    setVisibleSchemeCount(Math.min(SCHEME_BATCH_SIZE, schemes.length));
                                  }}
                                >
                                  <div>
                                    <strong>{scheme?.nombre || `Esquema ${index + 1}`}</strong>
                                    <span>
                                      Anticipo {formatPercent(toNumber(scheme?.anticipo, 0))} · Finiquito {formatPercent(toNumber(scheme?.finiquito, 0))}
                                    </span>
                                    <span>
                                      {Math.max(0, Math.round(toNumber(scheme?.mensualidades, 0)))} mensualidades · Descuento {formatPercent(toNumber(scheme?.descuento, 0))}
                                    </span>
                                  </div>
                                  {schemeIcon?.src && <img src={schemeIcon.src} alt={schemeIcon.alt} title={schemeIcon.title} className={styles.cotizador__schemeIcon} aria-hidden="true" />}
                                </button>
                              );
                            })}
                          </div>

                          {(hasMoreSchemes || canCollapseSchemes) && (
                            <div className={styles.cotizador__schemeActions}>
                              {hasMoreSchemes && (
                                <button
                                  type="button"
                                  className={styles.cotizador__schemeAction}
                                  onClick={() => {
                                    setVisibleSchemeCount((prev) => Math.min(prev + SCHEME_BATCH_SIZE, schemes.length));
                                  }}
                                >
                                  Ver más planes
                                </button>
                              )}

                              {canCollapseSchemes && (
                                <button
                                  type="button"
                                  className={styles.cotizador__schemeAction}
                                  onClick={() => {
                                    setVisibleSchemeCount(Math.min(
                                      schemes.length,
                                      Math.max(SCHEME_BATCH_SIZE, selectedSchemeIndex + 1)
                                    ));
                                  }}
                                >
                                  Ver menos
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {selectedScheme && (
                      <>
                        <div className={styles.cotizador__rangeGrid}>
                          {data?.permitirDescuentoCustom && (
                            <div className={styles.cotizador__inputGroup}>
                              <label className={styles.cotizador__inputLabel} htmlFor="cotizador-descuento-custom">
                                Descuento adicional
                              </label>
                              <input
                                id="cotizador-descuento-custom"
                                className={styles.cotizador__input}
                                type="number"
                                min="0"
                                max={maxCustomDiscount}
                                step="0.1"
                                value={customDiscount}
                                onChange={(event) => {
                                  const nextValue = clampNumber(toNumber(event.target.value, 0), 0, maxCustomDiscount);
                                  setCustomDiscount(nextValue);
                                }}
                              />
                              <span className={styles.cotizador__hint}>Máximo permitido: {formatPercent(maxCustomDiscount)}</span>
                            </div>
                          )}

                          <div className={styles.cotizador__inputGroup}>
                            <label className={styles.cotizador__inputLabel} htmlFor="cotizador-meses-diferido">
                              Meses de diferido
                            </label>
                            <input
                              id="cotizador-meses-diferido"
                              className={styles.cotizador__input}
                              type="number"
                              min={minDeferredMonths}
                              max={maxDeferredMonths}
                              value={mesesDiferido}
                              onChange={(event) => {
                                const nextValue = clampNumber(
                                  Math.round(toNumber(event.target.value, minDeferredMonths)),
                                  minDeferredMonths,
                                  maxDeferredMonths
                                );
                                setCustomDeferredMonths(nextValue);
                              }}
                            />
                            <span className={styles.cotizador__hint}>Rango disponible: {minDeferredMonths} a {maxDeferredMonths} meses</span>
                          </div>
                        </div>

                        {extras.length > 0 && (
                          <div>
                            <p className={styles.cotizador__inputLabel}>Adicionales</p>
                            <div className={styles.cotizador__extrasList}>
                              {extras.map((extra) => {
                                const isChecked = selectedExtraKeys.includes(extra.key);
                                return (
                                  <label key={extra.key} className={styles.cotizador__extraItem}>
                                    <input
                                      className={styles.cotizador__checkbox}
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => toggleExtra(extra.key)}
                                    />
                                    <span>{extra.nombre}</span>
                                    <strong>{formatCurrency(extra.precio)}</strong>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <p className={styles.cotizador__empty}>No hay esquemas de pago configurados en Strapi para este proyecto.</p>
                )}

                <div className={styles.cotizador__footer}>
                  <button type="button" className={styles.cotizador__btnBack} onClick={() => {
                    setIsSchemeMenuOpen(false);
                    setVisibleSchemeCount(Math.min(SCHEME_BATCH_SIZE, schemes.length));
                    setScreen(0);
                  }}>
                    <span className={styles.cotizador__btnArrow}>←</span>
                    <span className={styles.cotizador__btnText}>Atrás</span>
                  </button>
                  <button
                    type="button"
                    className={styles.cotizador__btnPrimary}
                    onClick={() => {
                      setIsSchemeMenuOpen(false);
                      setVisibleSchemeCount(Math.min(SCHEME_BATCH_SIZE, schemes.length));
                      setScreen(2);
                    }}
                    disabled={!selectedScheme}
                  >
                    <span className={styles.cotizador__btnText}>{stepLabels[1]}</span>
                    <span className={styles.cotizador__btnArrow}>→</span>
                  </button>
                </div>
              </div>
            )}

            {screen === 2 && (
              <div className={styles.cotizador__screen}>
                <div className={styles.cotizador__heading}>
                  <span className={styles.cotizador__headingBadge}>02</span>
                  <div className={styles.cotizador__headingContent}>
                    <h3 className={styles.cotizador__headingTitle}>{stepLabels[1]}</h3>
                    <p className={styles.cotizador__headingText}>{stepCopy.step2}</p>
                  </div>
                </div>

                <div className={styles.cotizador__calcGrid}>
                  <article className={styles.cotizador__calcCard}>
                    <span className={styles.cotizador__calcLabel}>Descuento</span>
                    <strong className={styles.cotizador__calcValue}>{formatCurrency(calculation.discountAmount)}</strong>
                    <span className={styles.cotizador__calcHelp}>{formatPercent(calculation.totalDiscountPct)} total</span>
                  </article>
                  <article className={styles.cotizador__calcCard}>
                    <span className={styles.cotizador__calcLabel}>Total a pagar</span>
                    <strong className={styles.cotizador__calcValue}>{formatCurrency(calculation.totalToPay)}</strong>
                    <span className={styles.cotizador__calcHelp}>Base {formatCurrency(priceValue)} + extras {formatCurrency(calculation.extrasTotal)}</span>
                  </article>
                  <article className={styles.cotizador__calcCard}>
                    <span className={styles.cotizador__calcLabel}>Enganche ({formatPercent(calculation.upfrontPct)})</span>
                    <strong className={styles.cotizador__calcValue}>{formatCurrency(calculation.upfrontAmount)}</strong>
                    <span className={styles.cotizador__calcHelp}>Primer pago</span>
                  </article>
                  <article className={styles.cotizador__calcCard}>
                    <span className={styles.cotizador__calcLabel}>Diferido ({formatPercent(calculation.deferredPct)})</span>
                    <strong className={styles.cotizador__calcValue}>{formatCurrency(calculation.deferredAmount)}</strong>
                    <span className={styles.cotizador__calcHelp}>Distribuido en {mesesDiferido} meses</span>
                  </article>
                  <article className={styles.cotizador__calcCard}>
                    <span className={styles.cotizador__calcLabel}>Mensualidades ({calculation.monthlyCount})</span>
                    <strong className={styles.cotizador__calcValue}>{formatCurrency(calculation.monthlyAmount)}</strong>
                    <span className={styles.cotizador__calcHelp}>Pago estimado por mensualidad</span>
                  </article>
                  <article className={styles.cotizador__calcCard}>
                    <span className={styles.cotizador__calcLabel}>Contra escritura ({formatPercent(calculation.finalPct)})</span>
                    <strong className={styles.cotizador__calcValue}>{formatCurrency(calculation.finalPayment)}</strong>
                    <span className={styles.cotizador__calcHelp}>Pago final</span>
                  </article>
                </div>

                {data?.textoLegal && <p className={styles.cotizador__legal}>{data.textoLegal}</p>}

                <div className={styles.cotizador__footer}>
                  <button type="button" className={styles.cotizador__btnBack} onClick={() => setScreen(1)}>
                    <span className={styles.cotizador__btnArrow}>←</span>
                    <span className={styles.cotizador__btnText}>Atrás</span>
                  </button>
                  <button type="button" className={styles.cotizador__btnPrimary} onClick={() => setScreen(3)}>
                    <span className={styles.cotizador__btnText}>{stepLabels[2]}</span>
                    <span className={styles.cotizador__btnArrow}>→</span>
                  </button>
                </div>
              </div>
            )}

            {screen === 3 && (
              <form ref={formRef} className={styles.cotizador__screen} onSubmit={handleSubmit} noValidate>
                <div className={styles.cotizador__heading}>
                  <span className={styles.cotizador__headingBadge}>03</span>
                  <div className={styles.cotizador__headingContent}>
                    <h3 className={styles.cotizador__headingTitle}>{stepLabels[2]}</h3>
                    <p className={styles.cotizador__headingText}>{stepCopy.step3}</p>
                  </div>
                </div>

                <div className={styles.cotizador__form}>
                  {groupedFields.map((group, index) => {
                    if (group.type === 'row') {
                      return (
                        <div key={`row-${index}`} className={styles.cotizador__fieldRow}>
                          {group.fields.map((field) => (
                            <div key={field.name} data-field-name={field.name} className={`${styles.cotizador__fieldGroup} ${styles['cotizador__fieldGroup--half']}`}>
                              <label className={styles.cotizador__label}>{field.label}</label>
                              {renderField(field)}
                              {fieldErrors[field.name] && (
                                <p id={`cotizador-error-${field.name}`} className={styles.cotizador__fieldError}>{fieldErrors[field.name]}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    }

                    return (
                      <div key={group.field.name} data-field-name={group.field.name} className={styles.cotizador__fieldGroup}>
                        <label className={styles.cotizador__label}>{group.field.label}</label>
                        {renderField(group.field)}
                        {fieldErrors[group.field.name] && (
                          <p id={`cotizador-error-${group.field.name}`} className={styles.cotizador__fieldError}>{fieldErrors[group.field.name]}</p>
                        )}
                      </div>
                    );
                  })}
                </div>

                {data?.textoLegal && <p className={styles.cotizador__legal}>{data.textoLegal}</p>}

                <div className={styles.cotizador__footer}>
                  <button type="button" className={styles.cotizador__btnBack} onClick={() => setScreen(2)}>
                    <span className={styles.cotizador__btnArrow}>←</span>
                    <span className={styles.cotizador__btnText}>Atrás</span>
                  </button>
                  <button type="submit" className={styles.cotizador__btnPrimary} disabled={submitting || !selectedScheme}>
                    <span className={styles.cotizador__btnText}>{submitting ? 'Enviando...' : 'Enviar cotización'}</span>
                    {!submitting && <span className={styles.cotizador__btnArrow}>→</span>}
                  </button>
                </div>
              </form>
            )}

            {screen === 4 && (
              <div className={styles.cotizador__screen}>
                <div className={styles.cotizador__heading}>
                  <span className={styles.cotizador__headingBadge}>04</span>
                  <div className={styles.cotizador__headingContent}>
                    <h3 className={styles.cotizador__headingTitle}>{stepLabels[3]}</h3>
                    <p className={styles.cotizador__headingText}>
                      {stepCopy.step4}
                    </p>
                  </div>
                </div>

                <div className={`${styles.cotizador__footer} ${styles['cotizador__footer--center']}`}>
                  <button type="button" className={`${styles.cotizador__btnPrimary} ${styles['cotizador__btnPrimary--compact']}`} onClick={() => onClose?.()}>
                    <span className={styles.cotizador__btnText}>Cerrar</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {toast && (
        <div className={`${styles.cotizador__toast} ${toast.type === 'success' ? styles['cotizador__toast--success'] : styles['cotizador__toast--error']}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default Cotizador;
